// strategyFamily.js — 전략 "의미 기반 표시 패밀리" 분류 (프론트엔드 전용).
//
// paper-trader 백엔드는 전략을 거친 4개 category(Macro/Volume/Liq/Other)로만
// 내려준다. 특히 Other 는 베이시스·페어·모멘텀이 전부 섞인 잡동사니 통이라
// "이게 무슨 전략인지" 한눈에 파악이 안 된다 (특히 모바일).
//
// 이 모듈은 strategyMeta.js 의 풍부한 paradigm 라벨을 사람이 이해할 수 있는
// 소수의 패밀리로 다시 묶는다. 서버/DB/Go 는 건드리지 않고 프론트 표시용으로만
// 재분류한다 (read-only 원칙 — strategyGrouping.js 와 동일 철학).
//
// 분류 우선순위: paradigm 문자열 키워드 매칭이 1순위, meta 가 없으면 백엔드
// category 로 폴백한다. 키워드 충돌(예: "Liq cross-coin divergence" 는 liq +
// cross-coin 둘 다 포함)은 아래 순서(거시→청산→베이시스→코인간→변동성→모멘텀)
// 로 해소한다. 즉 알파의 본질 소스를 우선한다.

import { getStrategyMeta } from "./strategyMeta";
import { readStrategyFields } from "../components/paper/PaperStrategyCard";

/**
 * 패밀리 표시 순서 (위 → 아래). "other" 는 항상 마지막.
 */
export const FAMILY_ORDER = [
    "liq",
    "basis",
    "crosscoin",
    "vol",
    "momentum",
    "macro",
    "newlisting",
    "other",
];

/**
 * 패밀리별 표시 메타 — 글리프(장식, aria-hidden), 한글 라벨, 한 줄 설명.
 * 색상 토큰은 섹션/카드 단에서 디자인 시스템 토큰으로 처리한다 (여기선 의미만).
 */
export const FAMILY_META = {
    liq: {
        key: "liq",
        glyph: "📉",
        label: "청산 캐스케이드",
        desc: "강제청산(liquidation)이 급증한 직후의 가격 과반응을 역·순방향으로 베팅",
    },
    basis: {
        key: "basis",
        glyph: "💱",
        label: "현·선물 베이시스",
        desc: "현물-선물 가격차(베이시스)가 벌어지면 다시 좁혀짐에 베팅",
    },
    crosscoin: {
        key: "crosscoin",
        glyph: "🔀",
        label: "코인 간 상대가격",
        desc: "한 코인이 다른 코인 대비 과도하게 벗어나면 되돌아옴에 베팅",
    },
    vol: {
        key: "vol",
        glyph: "⚡",
        label: "단기 변동성",
        desc: "5분 변동성이 급증한 직후의 단기 평균회귀 진입",
    },
    momentum: {
        key: "momentum",
        glyph: "📈",
        label: "추세 모멘텀",
        desc: "여러 시간대 추세가 동시에 같은 방향일 때 그 방향으로 진입",
    },
    macro: {
        key: "macro",
        glyph: "🌐",
        label: "거시 Risk-Off",
        desc: "거시 위험회피 환경 지표 기반 신호",
    },
    newlisting: {
        key: "newlisting",
        glyph: "🆕",
        label: "신규상장",
        desc: "Upbit 신규 상장 이벤트 직후의 가격 흐름(동시신규 breakdown / 기존코인 sell-the-news)에 베팅",
    },
    other: {
        key: "other",
        glyph: "📦",
        label: "기타",
        desc: "OI·실험 등 위 패밀리에 속하지 않는 전략",
    },
};

/**
 * paradigm(1순위) / category(폴백) 로 패밀리 키를 결정한다.
 * paradigm 키워드는 알파 본질 소스 우선순위로 매칭한다.
 *
 * @param {string|null|undefined} paradigm strategyMeta 의 paradigm 라벨
 * @param {string|null|undefined} category 백엔드 category (Macro/Volume/Liq/Other)
 * @returns {string} FAMILY_META 의 키
 */
export function classifyFamily(paradigm, category) {
    const p = typeof paradigm === "string" ? paradigm.toLowerCase() : "";
    if (p) {
        if (p.includes("new-listing") || p.includes("newlisting") || p.includes("신규상장")) return "newlisting";
        if (p.includes("macro")) return "macro";
        if (p.includes("liq")) return "liq";
        if (p.includes("basis")) return "basis";
        if (p.includes("cross-coin") || p.includes("cross coin")) return "crosscoin";
        if (p.includes("vol")) return "vol";
        if (p.includes("momentum")) return "momentum";
        // oi / variant / 기타 라벨 → 폴백으로 진행
    }
    const c = typeof category === "string" ? category.toLowerCase() : "";
    if (c === "macro") return "macro";
    if (c === "volume") return "vol";
    if (c === "liq") return "liq";
    return "other";
}

/**
 * 전략(단일 또는 코인 그룹) 1개의 패밀리 키를 구한다.
 * 그룹이면 첫 멤버의 meta.paradigm 을 사용한다 (멤버는 같은 base 전략).
 *
 * @param {object} s paper-trader 전략 객체 또는 groupStrategies 그룹 객체
 * @returns {string} FAMILY_META 의 키
 */
export function familyKeyOfStrategy(s) {
    let paradigm = null;
    if (s?.isGroup && Array.isArray(s.members) && s.members.length > 0) {
        const m0 = getStrategyMeta(readStrategyFields(s.members[0]).id);
        paradigm = m0?.paradigm;
    } else {
        const meta = getStrategyMeta(readStrategyFields(s).id);
        paradigm = meta?.paradigm;
    }
    const category = readStrategyFields(s).category ?? s?.category;
    return classifyFamily(paradigm, category);
}

/**
 * 전략 배열을 패밀리 키별 버킷으로 묶는다 (등장 순서 보존).
 *
 * @param {Array<object>} strategies
 * @returns {Record<string, Array<object>>} { familyKey: [strategies] }
 */
export function groupByFamily(strategies) {
    const out = {};
    if (!Array.isArray(strategies)) return out;
    for (const s of strategies) {
        const key = familyKeyOfStrategy(s);
        if (!out[key]) out[key] = [];
        out[key].push(s);
    }
    return out;
}

/**
 * 패밀리 버킷의 합산 PnL(KRW)을 구한다. 그룹 객체는 이미 합산된 pnl 을 가지므로
 * readStrategyFields(s).pnl 단순 합산으로 정확하다.
 *
 * @param {Array<object>} list
 * @returns {number} 합산 PnL (KRW)
 */
export function familyPnl(list) {
    if (!Array.isArray(list)) return 0;
    return list.reduce((sum, s) => sum + (readStrategyFields(s).pnl || 0), 0);
}

/**
 * 카드/배지에 쓸 패밀리 표시 정보(글리프+라벨)를 구한다.
 *
 * @param {object} s 전략 객체
 * @returns {{ key:string, glyph:string, label:string, desc:string }}
 */
export function familyBadge(s) {
    return FAMILY_META[familyKeyOfStrategy(s)] ?? FAMILY_META.other;
}
